<script setup>
import {reactive, onMounted, computed, watch, nextTick} from "vue";
import {ApiWrapper} from "@/Utils/ApiWrapper";
import {Permissions} from "@/Utils/Permissions";
import {ToastUtil} from "@/Utils/ToastUtil";

const props = defineProps({
  contextSchemaA: String
});

const data = reactive({
  search: '',
  dialog: false,
  dialogDelete: false,
  headers: [
    {
      title: 'Certifications',
      align: 'start',
      sortable: false,
      key: 'label',
    },
    { title: 'Actions', key: 'actions', sortable: false },
  ],
  editedIndex: -1,
  editedItem: {
    label: ''
  },
  defaultItem: {
    label: ''
  },
  certificationTypes: [],
  loaded: true,
  isEditing: false,
});

const formTitle = computed(()=>{
  return data.editedIndex === -1 ? 'New Item' : 'Edit Item'
});

watch(data.dialog,(val)=>{
  val || close()
})

watch(data.dialogDelete,(val)=>{
  val || closeDelete()
})

const editItem  = (item) => {
  data.isEditing = true
  data.editedIndex = data.certificationTypes.indexOf(item)
  data.editedItem = Object.assign({}, item)
  data.dialog = true
}

// deleteItem (item) {
//   data.editedIndex = data.certificationTypes.indexOf(item)
//   data.editedItem = Object.assign({}, item)
//   data.dialogDelete = true
// },

const deleteItemConfirm = () => {
  data.certificationTypes.splice(data.editedIndex, 1)
  closeDelete()
}

const deleteItem = (item) => {
  const deleteMessage = confirm('Are you sure you want to delete this item?');
  if (deleteMessage === true) {
    data.loading = true;
    ApiWrapper.post('certifications', 'delete-certifications-admin', { item: item })
        .then((response) => {
          getAllCertificationsAdmin();
          data.loading = false;

          ToastUtil.successMsg('Deleted certification');
        })
        .catch((err) => {
          ToastUtil.error(err);
          data.loading = false;
        });
  }
}

const close = () => {
  data.dialog = false
  nextTick(() => {
    data.editedItem = Object.assign({}, data.defaultItem)
    data.editedIndex = -1
    data.isEditing = false;
  })
}

const closeDelete = () => {
  data.dialogDelete = false
  nextTick(() => {
    data.editedItem = Object.assign({}, data.defaultItem)
    data.editedIndex = -1
  })
}

const getAllCertificationsAdmin = () => {
  ApiWrapper.post('certifications', 'get-certifications-admin', {})
      .then((response) => {
        data.certificationTypes = response.certifications;
        data.status = false;
      })
      .catch((e)=>{
        ToastUtil.error(e);
      })
      .finally(()=>{
        data.loading = false;
        data.loaded = true;
      });
}

const save = () => {
  data.loading = true
  if (data.isEditing) {
    ApiWrapper.post('certifications', 'edit-certifications-admin', {
          editedItem: data.editedItem,
        })
        .then((response) => {
          getAllCertificationsAdmin();
          ToastUtil.successMsg('Updated certification');
        })
        .catch((err) => {
          ToastUtil.error(err);
          data.loading = false;
        })
  } else {
    ApiWrapper.post('certifications', 'create-certifications-admin', {
          editedItem: data.editedItem,
        })
        .then((response) => {
          getAllCertificationsAdmin();
          ToastUtil.successMsg('Created certification');
        })
        .catch((err) => {
          ToastUtil.error(err);
          data.loading = false;
        })
  }
  close()
}

onMounted(()=>{
  getAllCertificationsAdmin();
})
</script>

<template>
  <v-container fluid v-if="data.loaded===true">
    <v-card>
      <v-data-table
          :headers="data.headers"
          :items="data.certificationTypes"
          :search="data.search"
          mobile-breakpoint="400"
      >
        <template v-slot:top>
          <v-toolbar
              flat
              density="compact"
              color="white"
          >
            <v-toolbar-title>Certifications Manager</v-toolbar-title>
            <v-divider
                class="mx-4"
                inset
                vertical
            ></v-divider>
            <v-spacer></v-spacer>
            <v-dialog
                v-model="data.dialog"
                max-width="500px"
            >
              <template v-slot:activator="{ on, props }">
                <v-btn
                    variant="elevated"
                    color="primary"
                    size="small"
                    dark
                    class="mb-2"
                    v-bind="props"
                    v-on="on"
                    v-if="Permissions.checkPermissions(['certifications.create-admin'])"
                >
                  Add
                </v-btn>
              </template>
              <v-card>
                <v-card-title>
                  <span class="headline">{{ formTitle }}</span>
                </v-card-title>
                <v-card-text>
                  <v-container>
                    <v-row>
                      <v-col
                          cols="12"
                          sm="6"
                          md="4"
                      >
                        <v-text-field
                            v-model="data.editedItem.label"
                            label="Name"
                        ></v-text-field>
                      </v-col>
                    </v-row>
                  </v-container>
                </v-card-text>
                <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn
                      color="blue darken-1"
                      variant="text"
                      @click="close"
                  >
                    Cancel
                  </v-btn>
                  <v-btn
                      color="blue darken-1"
                      variant="text"
                      @click="save"
                      :disabled="data.editedItem.label === '' || data.editedItem.label === null"
                  >
                    Save
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
            <v-dialog v-model="data.dialogDelete" max-width="500px">
              <v-card>
                <v-card-title class="headline">Are you sure you want to delete this item?</v-card-title>
                <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn color="blue darken-1" variant="text" @click="closeDelete">Cancel</v-btn>
                  <v-btn color="blue darken-1" variant="text" @click="deleteItemConfirm">OK</v-btn>
                  <v-spacer></v-spacer>
                </v-card-actions>
              </v-card>
            </v-dialog>
          </v-toolbar>
          <v-row>
            <v-col
                cols="12"
                sm="6"
            >
              <v-text-field
                  v-model="data.search"
                  append-icon="mdi-magnify"
                  label="Search"
                  single-line
                  hide-details
                  class="mx-4"
              ></v-text-field>
            </v-col>
          </v-row>
        </template>
        <template v-slot:[`item.actions`]="{ item }">
          <v-icon
              size="small"
              class="mr-2"
              v-if="Permissions.checkPermissions(['certifications.update-admin'])"
              @click="editItem(item)"
          >
            mdi-pencil
          </v-icon>
          <v-icon
              size="small"
              v-if="Permissions.checkPermissions(['certifications.delete-admin'])"
              @click="deleteItem(item)"
          >
            mdi-delete
          </v-icon>
        </template>
      </v-data-table>
    </v-card>
  </v-container>
</template>

<style scoped>
.container {
  padding: 5px;
}

[v-cloak] {
  display: none;
}
</style>